import * as Yup from 'yup';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import compact from 'lodash/compact';
import { Step, StepLabel, StepButton } from '@mui/material';

import {
    useAuth,
    useComposition,
    useFrami,
    useOrder,
    usePrice,
    CheckoutContext,
    useCompositionTemplates,
} from '../context';
import { uiColors } from '../common/styles';
import { phoneRegex, postalRegex, FRAMEVOUCHER } from '../data/constants';
import { useTracking } from '../data/gtm';
import { googleAddressValidation, vatNumberValidation } from '../utilities/externalApi';
import { assignFrameCountGroup, getLUTFiltercategory, hasMargin } from '../utilities/compositionTemplate';
import { calculateConversion } from '../data/gtm';
import { logError } from '../utilities/logError';

export function CheckoutProvider(props) {
    const { currentComp, updateCompHangingSystem, updateCompPaper } = useComposition();
    const { generate_sku } = useCompositionTemplates();
    const [activeStep, setActiveStep] = useState(0);
    const [templateSelected, setTemplateSelected] = useState(currentComp?.hanging_system);
    const { eventsCounter, setEventsCounter, updateGoogleEventsCounter } = useAuth();
    const { currentFrami, memoizedPapers, memoizedImageFilters, updateFramiHangingSystem } = useFrami();
    const {
        calculateCompositionPrices,
        getPricePLN,
        getCompositionPrintPrice,
        getCompositionHangersPrice,
        formatPrice,
        getCouponDiscountPercentage,
    } = usePrice();
    const { order, updateItemHangingSystem, updateItemPaper, updateOrder, reloadOrder } = useOrder();
    const intl = useIntl();
    const { pushEvent } = useTracking();

    const beginCheckout = () => {

        const updatedEventsCount = (eventsCounter['begin_checkout'] || 0) + 1;
        setEventsCounter((prev) => ({ ...prev, begin_checkout: updatedEventsCount }));
        updateGoogleEventsCounter({ ...eventsCounter, begin_checkout: updatedEventsCount });
        const percentValue = calculateConversion('begin_checkout', updatedEventsCount);

        pushEvent('begin_checkout', {
            currency: order.currency,
            value: order.price_before_discount_gross * percentValue,
            discounted: order.price_after_discount_gross,
            price_in_pln: order.price_before_discount_gross_in_pln,
            price_discounted_in_pln: order.price_after_discount_gross_in_pln,
            items: order.compositions.map((obj) => {
                const lutFilterCategory = getLUTFiltercategory(memoizedImageFilters.get(obj?.lut_filter))
                const framesCountGroup = assignFrameCountGroup(obj?.frames_json.length);
                const margin = hasMargin(currentFrami.default_passepartout_width)
                return ({
                    item_id: generate_sku(obj?.category, lutFilterCategory, framesCountGroup, margin),
                    item_category: obj?.category,
                    item_category2: lutFilterCategory,
                    item_category3: framesCountGroup,
                    item_category4: margin,
                    quantity: 1,
                })
            }),
        });
        setActiveStep(1);
        reloadOrder(true);
    };

    const handleHangingSelect = (compositionToChange, hangingSystem) => {
        setTemplateSelected(hangingSystem);
        if (
            !order ||
            order.compositions.length === 0 ||
            !order.compositions.some((c) => c.id === compositionToChange.id)
        ) {
            updateCompHangingSystem(hangingSystem, hangingSystem === 'T' ? currentComp.frames_json.length : 0);
        } else if (order) {
            const updatedComps = order.compositions.map((composition) => {
                if (
                    !(composition.is_voucher && composition.voucher_type !== FRAMEVOUCHER) &&
                    composition.id === compositionToChange.id
                ) {
                    const hangers = hangingSystem === 'T' ? composition.frames_json.length : 0;

                    const updatedComp = {
                        ...composition,
                        hanging_system: hangingSystem,
                        hangers_count: hangers,
                        order_details: {
                            ...composition.order_details,
                            hangers: hangers,
                            template: hangingSystem,
                        },
                    };
                    updateItemHangingSystem(updatedComp, hangingSystem, hangers);

                    const calculatedPrices = calculateCompositionPrices(updatedComp, updatedComp.paper, order);

                    return {
                        ...updatedComp,
                        price: calculatedPrices,
                        price_in_pln: getPricePLN(calculatedPrices.rounded.total, currentFrami?.currency),
                    };
                }
                return composition;
            });

            updateOrder({ ...order, compositions: updatedComps });
        } else {
            updateCompHangingSystem(hangingSystem, hangingSystem === 'T' ? currentComp.frames_json.length : 0);
        }
        updateFramiHangingSystem(hangingSystem)
    };

    const hangersOptions = useCallback(
        (compositionToChange = null) => {
            let totalHangersPrice = 0;
            let hangersDiscount = 0;
            let hangersDiscountPrice = 0;
            const coupon = currentFrami?.coupon || order?.coupon;
            const isPercentageCoupon =
                coupon &&
                coupon.discount_tiers &&
                !(!compositionToChange.is_sample && coupon.code.startsWith('SAMPLE'));
            if (compositionToChange) {
                totalHangersPrice = getCompositionHangersPrice(compositionToChange.frames_json.length ?? 0);
                if (isPercentageCoupon) {
                    const discountPercentage = getCouponDiscountPercentage(coupon, totalHangersPrice, currentFrami.currency);
                    const couponValue = discountPercentage / 100;
                    hangersDiscount = totalHangersPrice * couponValue;
                } else {
                    hangersDiscount = 0;
                }
                hangersDiscountPrice = totalHangersPrice - hangersDiscount;
            }

            const options = currentFrami && [
                {
                    id: 'M',
                    title: intl.formatMessage({ id: 'provider.checkout.options.hanging.option_2.title' }),
                    imageSrc: '/assets/checkout/template_wo_hangers_hi_res.png',
                    buttonTitle: intl.formatMessage({ id: 'studio.button.select' }),
                    headers: [
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.header_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_2.header_2' }),
                    ],
                    descriptions: [
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_2.description_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_2.description_2' }),
                    ],
                    warning: intl.formatMessage({ id: 'provider.checkout.options.hanging.option_2.warning'}),
                    onSelect: () => {
                        handleHangingSelect(compositionToChange, 'M');
                    },
                    price: intl.formatMessage({ id: 'studio.included' }),
                },                
                {
                    id: 'T',
                    title: intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.title' }),
                    imageSrc: '/assets/checkout/hangers.png',
                    additionalImageSrc: '/assets/checkout/template_w_hangers_hi_res.png',
                    buttonTitle: intl.formatMessage({ id: 'studio.button.action.basket' }),
                    headers: [
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.header_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.header_2' }),
                    ],
                    descriptions: [
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.description_1' }),
                        'pros',
                    ],
                    pros: [
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.pros_4' }),
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.hanging.option_1.pros_3' }),
                    ],
                    onSelect: () => {
                        handleHangingSelect(compositionToChange, 'T');
                    },
                    price: formatPrice(totalHangersPrice),
                    discountedPrice: formatPrice(hangersDiscountPrice),
                    discountPercentage: isPercentageCoupon ? getCouponDiscountPercentage(coupon, totalHangersPrice, currentFrami.currency) : null,
                    label: 'Bestseller',
                }

            ];

            return options;
        },
        [currentComp, order]
    );

    const paperOptions = useCallback(
        (compositionToChange) => {
            let standardPaper = 0;
            let fineArtPaper = 0;
            let hahnemuhlePaper = 0;

            const coupon = currentFrami?.coupon || order?.coupon;
            const isPercentageCoupon = coupon && coupon.discount_tiers;
            let couponValue = 0;
            if (isPercentageCoupon) {
                const discountPercentage = getCouponDiscountPercentage(coupon, fineArtPaper - standardPaper, currentFrami.currency);
                couponValue = discountPercentage / 100;
            }

            if (compositionToChange) {
                const standard = memoizedPapers.get('STANDARD');
                const premium_1 = memoizedPapers.get('FINEART');
                const premium_2 = memoizedPapers.get('HAHNEMUHLE');

                standardPaper += getCompositionPrintPrice(compositionToChange.order_details.images, standard);
                fineArtPaper += getCompositionPrintPrice(compositionToChange.order_details.images, premium_1);
                hahnemuhlePaper += getCompositionPrintPrice(compositionToChange.order_details.images, premium_2);
            }

            const handlePaperSelect = (paper) => {
                if (
                    !order ||
                    order.compositions.length === 0 ||
                    !order.compositions.some((c) => c.id === compositionToChange.id)
                ) {
                    updateCompPaper(paper);
                } else if (order) {
                    const updatedComps = order.compositions.map((composition) => {
                        if (
                            !(composition.is_voucher && composition.voucher_type !== 'FRAMEVOUCHER') &&
                            compositionToChange.id === composition.id
                        ) {
                            updateItemPaper(composition, paper);
                            const calculatedPrices = calculateCompositionPrices(composition, paper, order);

                            return {
                                ...composition,
                                price: calculatedPrices,
                                paper: paper,
                                price_in_pln: getPricePLN(calculatedPrices.rounded.total, currentFrami?.currency),
                            };
                        }
                        return composition;
                    });

                    updateOrder({ ...order, compositions: updatedComps });
                }
            };

            const options = currentFrami && [
                {
                    id: 'STANDARD',
                    title: intl.formatMessage({ id: 'provider.checkout.options.paper.option_1.title' }),
                    description: intl.formatMessage({ id: 'provider.checkout.options.paper.option_1.description' }),
                    buttonTitle: intl.formatMessage({ id: 'studio.button.select' }),
                    pros: [
                        intl.formatMessage({ id: 'provider.checkout.options.paper.standard.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.standard.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.common.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.common.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.standard.pros_3' }),
                    ],
                    onSelect: () => {
                        handlePaperSelect('STANDARD');
                    },
                    price: intl.formatMessage({ id: 'studio.included' }),
                    label: 'Bestseller',
                },
                {
                    id: 'FINEART',
                    title: intl.formatMessage({ id: 'provider.checkout.options.paper.option_2.title' }),
                    description: intl.formatMessage({ id: 'provider.checkout.options.paper.option_2.description' }),
                    buttonTitle: intl.formatMessage({ id: 'studio.button.action.basket' }),
                    pros: [
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.common.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.common.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_3' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_4' }),
                    ],
                    onSelect: () => {
                        handlePaperSelect('FINEART');
                    },
                    price: formatPrice(fineArtPaper - standardPaper),
                    discountedPrice: formatPrice((fineArtPaper - standardPaper) * (1 - couponValue)),
                    discountPercentage: isPercentageCoupon ? getCouponDiscountPercentage(coupon, fineArtPaper - standardPaper, currentFrami.currency) : null,
                    label: 'Premium',
                },
                {
                    id: 'HAHNEMUHLE',
                    title: intl.formatMessage({ id: 'provider.checkout.options.paper.option_3.title' }),
                    description: intl.formatMessage({ id: 'provider.checkout.options.paper.option_3.description' }),
                    buttonTitle: intl.formatMessage({ id: 'studio.button.action.basket' }),
                    pros: [
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.common.pros_1' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.common.pros_2' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_3' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_4' }),
                        intl.formatMessage({ id: 'provider.checkout.options.paper.premium.pros_5' }),
                    ],
                    onSelect: () => {
                        handlePaperSelect('HAHNEMUHLE');
                    },
                    price: formatPrice(hahnemuhlePaper - standardPaper),
                    discountedPrice: formatPrice(hahnemuhlePaper - standardPaper * (1 - couponValue)),
                    discountPercentage: isPercentageCoupon ? getCouponDiscountPercentage(coupon, hahnemuhlePaper - standardPaper, currentFrami.currency) : null,
                    label: 'Premium',
                },
            ];

            return options;
        },
        [currentComp, order]
    );

    const stringValidation = () =>
        Yup.string()
            .min(2, intl.formatMessage({ id: 'provider.checkout.validation.too_short' }))
            .max(50, intl.formatMessage({ id: 'provider.checkout.validation.too_long' }))
            .required(intl.formatMessage({ id: 'provider.checkout.validation.required' }));

    const conditionalRequired = (message, fieldToCompare) => {
        return Yup.string().test('conditional-required', message, function (value) {
            const parent = this.parent || this.options.context;
            const fieldValue = parent[fieldToCompare];

            if (fieldValue && !value) return false;
            return true;
        });
    };

    const getCharacterValidationError = (str) => {
        return intl.formatMessage({ id: `studio.account.email.validation.${str}` });
    };

    const validateFullAddress = async (values, isBillingAddress = false) => {
        // possible missing components: [street_number, route, locality, postal_code, country]

        try {
            const validationResult = await googleAddressValidation({
                street: isBillingAddress ? values.billingAddress : values.shippingAddress,
                city: isBillingAddress ? values.billingCity : values.shippingCity,
                postalCode: isBillingAddress ? values.billingPostal : values.shippingPostal,
                country: isBillingAddress ? values.billingCountry : values.shippingCountry,
            });

            const isMissingOrUnconfirmed = (type) =>
                validationResult.missingComponentTypes?.includes(type) ||
                validationResult.addressComponents?.some((el) => el.componentType === type && el.replaced === true) ||
                validationResult.unconfirmedComponentTypes?.includes(type);

            let errors = {};

            if (isMissingOrUnconfirmed('country')) {
                var countryError = intl.formatMessage({ id: 'provider.checkout.validation.invalid_country' });
                if (isBillingAddress) {
                    errors.billingCountry = countryError;
                } else {
                    errors.shippingCountry = countryError;
                }
            } else {
                if (isMissingOrUnconfirmed('route')) {
                    var routeError = intl.formatMessage({ id: 'provider.checkout.validation.invalid_street_name' });
                    if (isBillingAddress) {
                        errors.billingAddress = routeError;
                    } else {
                        errors.shippingAddress = routeError;
                    }
                } else if (validationResult.missingComponentTypes?.includes('street_number')) {
                    var streetError = intl.formatMessage({ id: 'provider.checkout.validation.missing_street_number' });

                    if (isBillingAddress) {
                        errors.billingAddress = streetError;
                    } else {
                        errors.shippingAddress = streetError;
                    }
                }

                if (isMissingOrUnconfirmed('postal_code')) {
                    var postalError = intl.formatMessage({ id: 'provider.checkout.validation.invalid_postal_code' });
                    if (isBillingAddress) {
                        errors.billingPostal = postalError;
                    } else {
                        errors.shippingPostal = postalError;
                    }
                }

                if (isMissingOrUnconfirmed('locality')) {
                    var cityError = intl.formatMessage({ id: 'provider.checkout.validation.invalid_locality' });
                    if (isBillingAddress) {
                        errors.billingCity = cityError;
                    } else {
                        errors.shippingCity = cityError;
                    }
                }

                if (validationResult.unconfirmedComponentTypes?.includes('street_number')) {
                    errors.unconfirmedShippingStreet = intl.formatMessage({
                        id: 'provider.checkout.validation.unconfirmed_street_number',
                    });
                }
            }
            return { validationResult, errors };
        } catch (err) {
            logError(err);
            throw err;
        }
    };

    const ShippingInfoSchema = Yup.object().shape({
        shippingFirstName: stringValidation(),
        shippingLastName: stringValidation(),
        userEmail: Yup.string()
            .email(intl.formatMessage({ id: 'provider.checkout.validation.invalid_email' }))
            .required(intl.formatMessage({ id: 'provider.checkout.validation.required' })),
        shippingPhoneNumber: Yup.string()
            .matches(phoneRegex, intl.formatMessage({ id: 'provider.checkout.validation.invalid_phone_number' }))
            .required(intl.formatMessage({ id: 'provider.checkout.validation.required' })),
        shippingAddress: stringValidation(),
        shippingCity: stringValidation(),
        shippingCountry: stringValidation(),
        shippingPostal: Yup.string()
            .matches(postalRegex, intl.formatMessage({ id: 'provider.checkout.validation.invalid_postal_code' }))
            .required(intl.formatMessage({ id: 'provider.checkout.validation.required' })),
        password: Yup.string()
            .min(8, intl.formatMessage({ id: 'studio.account.email.min_length' }))
            .matches(/[0-9]/, getCharacterValidationError('digit'))
            .matches(/[a-z]/, getCharacterValidationError('lowercase'))
            .matches(/[A-Z]/, getCharacterValidationError('uppercase')),
        confirmPassword: Yup.string().oneOf(
            [Yup.ref('password')],
            intl.formatMessage({ id: 'studio.account.password.validation.match' })
        ),
        marketingAccepted: Yup.boolean(),
        needInvoice: Yup.boolean(),
        companyName: conditionalRequired(
            intl.formatMessage({ id: 'provider.checkout.validation.required' }),
            'needInvoice'
        ),
        vatNumber: conditionalRequired(
            intl.formatMessage({ id: 'provider.checkout.validation.required' }),
            'needInvoice'
        ).test(
            'check-vat',
            intl.formatMessage({ id: 'provider.checkout.validation.invalid_vat' }),
            async function (value) {
                const { needInvoice, billingCountry } = this.parent;
                if (!needInvoice) return true;

                try {
                    return await vatNumberValidation(billingCountry, value);
                } catch (error) {
                    return this.createError({ message: error.message });
                }
            }
        ),
        billingAddress: Yup.string(),
        billingPostal: Yup.string().matches(
            postalRegex,
            intl.formatMessage({ id: 'provider.checkout.validation.invalid_postal_code' })
        ),
        billingCity: Yup.string(),
        billingCountry: Yup.string(),
    });

    const steps = compact([
        intl.formatMessage({ id: 'checkout.steps.start' }),
        intl.formatMessage({ id: 'checkout.steps.shipping' }),
        intl.formatMessage({ id: 'checkout.steps.payment' }),
    ]);

    const stepsLabels = useMemo(() => {
        return steps.map((label, idx) => {
            const labelProps = {};

            return (
                <Step
                    sx={{
                        '& .MuiStepConnector-root': { top: '9px' },
                        '& .MuiSvgIcon-root': { fontSize: '20px' },
                        '& .MuiStepLabel-label.Mui-active': { color: uiColors.PRIMARY_TEXT },
                    }}
                    style={{ padding: `0 ${idx}px` }}
                    key={label}
                >
                    <StepButton onClick={() => setActiveStep(idx)}>
                        <StepLabel {...labelProps}>{label}</StepLabel>
                    </StepButton>
                </Step>
            );
        });
    }, [steps]);

    const value = {
        activeStep,
        beginCheckout,
        stepsLabels,
        hangersOptions,
        paperOptions,
        ShippingInfoSchema,
        setActiveStep,
        setTemplateSelected,
        templateSelected,
        handleHangingSelect,
        validateFullAddress,
    };

    return <CheckoutContext.Provider value={value} {...props} />;
}
